import { Children, isValidElement, useId, type MouseEvent, type ReactNode } from 'react';
import { SwitchControl } from '../Switch/Switch';
import { CaretRight } from '@phosphor-icons/react';
import { cx } from '../cx';
import { devWarn } from '../dev';
import { Icon } from '../Icon';

export type ListRowProps = {
  title: ReactNode;
  sub?: ReactNode;
  /** An IconBadge, LogoTile or Avatar. */
  leading?: ReactNode;
  /** A Chip or a count. With `href`/`onClick` and no trailing, a chevron is drawn. For a switch row, use `toggle`. */
  trailing?: ReactNode;
  href?: string;
  /** Receives the click event, so a linked row can intercept it for a soft client-side navigation. */
  onClick?: (event: MouseEvent<HTMLElement>) => void;
  /** A toggle row: the whole row is the label for a trailing switch (settings, alert rows). */
  toggle?: { checked: boolean; onCheckedChange: (checked: boolean) => void; disabled?: boolean };
  className?: string;
};

/** One row in a grouped list. At least 56px tall; the whole row is the target. Group rows in ListGroup. */
export function ListRow({ title, sub, leading, trailing, href, onClick, toggle, className }: ListRowProps) {
  const toggleId = `lt${useId().replace(/:/g, '')}`;
  devWarn(
    Boolean(toggle && (href || onClick)),
    'ListRow: a toggle row is the label of its switch; href and onClick are ignored.',
  );
  const pressable = !toggle && !!(href || onClick);
  const body = (
    <>
      {leading ? <span className="flex shrink-0">{leading}</span> : null}
      <span className="flex min-w-0 flex-1 flex-col">
        <span className="truncate text-sm font-semibold text-jc-ink">{title}</span>
        {sub ? <span className="truncate text-sm text-jc-ink-secondary">{sub}</span> : null}
      </span>
      {toggle ? (
        <SwitchControl
          id={toggleId}
          checked={toggle.checked}
          onCheckedChange={toggle.onCheckedChange}
          disabled={toggle.disabled}
        />
      ) : (
        trailing
      )}
      {pressable && !trailing ? <Icon icon={CaretRight} size="sm" className="shrink-0 text-jc-ink-secondary" /> : null}
    </>
  );
  const rowClass = cx(
    'flex min-h-14 w-full items-center gap-3 px-4 py-2 text-left',
    pressable &&
      'transition-colors duration-(--jc-duration-fast) hover:bg-jc-elevated focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-jc-focus',
    className,
  );
  if (toggle)
    return (
      <label
        htmlFor={toggleId}
        className={cx(rowClass, toggle.disabled ? 'cursor-not-allowed opacity-50' : 'cursor-pointer')}
      >
        {body}
      </label>
    );
  if (href)
    return (
      <a href={href} onClick={onClick} className={rowClass}>
        {body}
      </a>
    );
  if (onClick)
    return (
      <button type="button" onClick={onClick} className={rowClass}>
        {body}
      </button>
    );
  return <div className={rowClass}>{body}</div>;
}

export type ListGroupProps = {
  children: ReactNode;
  /** Optional heading above the group (a section-title, not a card title). */
  title?: ReactNode;
  className?: string;
};

/** Rows inside one card with hairline dividers, never a card per row. Use ListRow's toggle for a switch row. */
export function ListGroup({ children, title, className }: ListGroupProps) {
  return (
    <section className={cx('flex flex-col gap-2', className)}>
      {title ? <h3 className="px-1 type-eyebrow text-jc-ink-secondary">{title}</h3> : null}
      <ul className="divide-y divide-jc-border overflow-hidden rounded-jc-container border border-jc-card-border bg-jc-surface">
        {wrap(children)}
      </ul>
    </section>
  );
}

function wrap(children: ReactNode) {
  return Children.toArray(children).map((child, i) => (
    <li key={isValidElement(child) && child.key != null ? child.key : i}>{child}</li>
  ));
}
